/* eslint-disable react/jsx-no-useless-fragment */
import { Autocomplete, Button, CircularProgress, MenuItem, Select, TextField, Typography } from "@mui/material";
import styles from "./newPaymentCards.module.scss";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/page/store";
import {
  fetchGroupDetailAction,
  fetchGroupListAction,
  singlePayAction,
  getGroupCardAction,
  getGroupUserAction,
  resetGroupDetailAction,
  resetGroupCardAction,
  resetGroupUserAction
} from "src/page/newPayment/newPaymentSlice";
import moment from "moment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { EMPTY_STRING, INTERNAL_ADMIN } from "../constant/constant";
import { Group } from "src/page/group/groupSlice";
import { Card } from "src/page/cards/cardSlice";
import { GroupUser, GroupWallet } from "src/page/groupDetail/groupDetailSlice";
import { getDecimalNumber } from "src/utils/utils";

interface singlePayProp {
  onClose: (_value: boolean) => void;
  cb?: () => void;
}

export default function GroupPay({ onClose, cb }: singlePayProp) {
  const dispatch = useAppDispatch();
  const {
    groupsLoading,
    groups,
    groupCard,
    groupCardLoading,
    groupDetail,
    groupDetailLoading,
    groupUser,
    groupUserLoading,
    singlePayLoading
  } = useAppSelector((state) => state.newPayment);
  const { userInfo } = useAppSelector((state) => state.auth);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [selectedUser, setSelectedUser] = useState<GroupUser | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<GroupWallet | null>(null);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [timeDuration, setTimeDuration] = useState(EMPTY_STRING);
  // const [searchInput, setSearchInput] = useState<string>(EMPTY_STRING);
  const [error, setError] = useState({
    group: EMPTY_STRING,
    user: EMPTY_STRING,
    wallet: EMPTY_STRING,
    card: EMPTY_STRING,
    time: EMPTY_STRING
  });

  useEffect(() => {
    const data = {
      size: 100,
      from: 0,
      filter: {}
    };

    dispatch(fetchGroupListAction(data));
  }, [dispatch]);

  useEffect(() => {
    setSelectedWallet(null);
    setSelectedCard(null);
    setTimeDuration(EMPTY_STRING);
    if (selectedGroup) {
      dispatch(fetchGroupDetailAction(selectedGroup?.group_id));
    } else {
      dispatch(resetGroupDetailAction());
      dispatch(resetGroupCardAction());
      dispatch(resetGroupUserAction());
    }
  }, [selectedGroup?.group_id, userInfo?.role]);

  useEffect(() => {
    setSelectedCard(null);
    setSelectedUser(null);
    setTimeDuration(EMPTY_STRING);
    if (selectedGroup && selectedWallet) {
      dispatch(getGroupCardAction(selectedGroup?.group_id));
      if (userInfo?.role === INTERNAL_ADMIN) {
        dispatch(getGroupUserAction(selectedGroup?.group_id));
      }
    } else {
      dispatch(resetGroupCardAction());
      dispatch(resetGroupUserAction());
    }
  }, [selectedGroup, selectedWallet, userInfo?.role]);

  const handleBack = () => {
    onClose(false);
  };
  const handleConfirm = () => {
    if (
      !selectedGroup?.group_id ||
      !selectedWallet?.wallet_id ||
      !selectedCard?.id ||
      !timeDuration ||
      (userInfo?.role === INTERNAL_ADMIN && !selectedUser?.user_id)
    ) {
      setError({
        group: selectedGroup?.group_id ? EMPTY_STRING : "Select a group",
        wallet: selectedWallet?.wallet_id ? EMPTY_STRING : "Selected a wallet",
        card: selectedCard?.id ? EMPTY_STRING : "Select a card",
        time: timeDuration ? EMPTY_STRING : "Select a time duration",
        user: userInfo?.role === INTERNAL_ADMIN && selectedUser?.user_id ? EMPTY_STRING : "Select a user"
      });
      return;
    }

    let data;

    if (timeDuration !== "ONCE") {
      data = {
        userId: userInfo?.role === INTERNAL_ADMIN ? selectedUser?.user_id : userInfo?.id,
        cardId: String(selectedCard?.id),
        expireAt: moment()
          .add(Number(timeDuration || 3), "hours")
          .toISOString(),
        walletId: String(selectedWallet?.wallet_id)
      };
    } else {
      data = {
        userId: userInfo?.role === INTERNAL_ADMIN ? selectedUser?.user_id : userInfo?.id,
        cardId: String(selectedCard?.id),
        linkType: "ONCE",
        walletId: String(selectedWallet?.wallet_id)
      };
    }
    dispatch(
      singlePayAction(data, () => {
        setSelectedGroup(null);
        setSelectedWallet(null);
        setSelectedCard(null);
        setTimeDuration(EMPTY_STRING);
        if (cb) cb();
      })
    );
  };

  return (
    <div className={styles.newPaymentCard}>
      <header>
        <h1>Add New Group Payment</h1>
      </header>
      <main>
        <div>
          <Autocomplete
            options={groups?.list}
            getOptionLabel={(option) => option?.name ?? option}
            // filterOptions={(x) => x}
            // onInputChange={handleUserChange}
            value={selectedGroup}
            onChange={(event, newValue) => {
              setSelectedGroup(newValue);
              setError({
                ...error,
                group: EMPTY_STRING
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Group"
                aria-label="select group"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {groupsLoading ? (
                        <CircularProgress size={20} style={{ marginRight: 10 }} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  )
                }}
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option?.group_id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start"
                }}
              >
                <span>{option?.name}</span>
                <span style={{ color: "#64748B" }}>{option?.description}</span>
              </li>
            )}
          />
          {error.group && (
            <div className={styles.error}>
              <ErrorOutlineIcon className={styles.errIcon} />
              {error.group}
            </div>
          )}
        </div>
        <div>
          <Autocomplete
            options={groupDetail?.wallets}
            filterOptions={(x) => x}
            getOptionLabel={(option) => option.name || EMPTY_STRING}
            value={selectedWallet}
            onChange={(event, newValue) => {
              setSelectedWallet(newValue);
              setError({
                ...error,
                wallet: EMPTY_STRING
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Wallet"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {groupDetailLoading ? (
                        <CircularProgress size={20} style={{ marginRight: 10 }} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  )
                }}
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option?.wallet_id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start"
                }}
              >
                <span>{option?.name}</span>
                <span style={{ fontSize: "12px" }}>
                  Balance:
                  <span style={{ color: "green" }}> ${getDecimalNumber(option?.balance)}</span>
                </span>
              </li>
            )}
          />
          {error.wallet && (
            <div className={styles.error}>
              <ErrorOutlineIcon className={styles.errIcon} />
              {error.wallet}
            </div>
          )}
        </div>
        <div>
          <Autocomplete
            options={groupCard?.list}
            getOptionLabel={(option) => `${option?.cardHolderName ?? option?.user?.fullName}`}
            value={selectedCard}
            filterOptions={(x) => x}
            onChange={(event, newValue) => {
              setSelectedCard(newValue);
              setError({
                ...error,
                card: EMPTY_STRING
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Card"
                placeholder="select card for internal admin"
                aria-label="select card"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {groupCardLoading ? (
                        <CircularProgress size={20} style={{ marginRight: 10 }} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  )
                }}
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option?.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start"
                }}
              >
                <span>{option?.cardHolderName ?? option?.user?.fullName}</span>
                <span>{option?.cardNumber ? "xxxx-xxxx-xxxx-" + option?.cardNumber?.slice(-4) : EMPTY_STRING}</span>
              </li>
            )}
          />
          {error.card && (
            <div className={styles.error}>
              <ErrorOutlineIcon className={styles.errIcon} />
              {error.card}
            </div>
          )}
        </div>
        {userInfo?.role === INTERNAL_ADMIN && (
          <>
            <div>
              <Autocomplete
                options={groupUser?.list}
                getOptionLabel={(option) => option?.attributes?.first_name + " " + option?.attributes?.last_name}
                filterOptions={(x) => x}
                value={selectedUser}
                onChange={(event, newValue) => {
                  setSelectedUser(newValue);
                  setError({
                    ...error,
                    user: EMPTY_STRING
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select User"
                    aria-label="select user"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {groupUserLoading ? (
                            <CircularProgress size={20} style={{ marginRight: 10 }} />
                          ) : (
                            params.InputProps.endAdornment
                          )}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option?.user_id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start"
                    }}
                  >
                    <span>{option?.attributes?.first_name + " " + option?.attributes?.last_name}</span>
                    <span style={{ color: "#64748B" }}>{option?.email}</span>
                  </li>
                )}
              />
              {error.user && (
                <div className={styles.error}>
                  <ErrorOutlineIcon className={styles.errIcon} />
                  {error.user}
                </div>
              )}
            </div>
          </>
        )}
        <div>
          <Select
            value={timeDuration}
            onChange={(e) => {
              setTimeDuration(e.target.value);
              setError({
                ...error,
                time: EMPTY_STRING
              });
            }}
            displayEmpty
            aria-label="select time period"
            inputProps={{ "aria-label": "Without label" }}
            className={styles.select}
            renderValue={
              timeDuration !== EMPTY_STRING
                ? undefined
                : () => <Typography sx={{ color: "grey" }}>How long do you need the money?</Typography>
            }
          >
            <MenuItem value="ONCE">ONCE</MenuItem>
            <MenuItem value="2">2 Hours</MenuItem>
            <MenuItem value="4">4 Hours</MenuItem>
            <MenuItem value="8">8 Hours</MenuItem>
            <MenuItem value="12">12 Hours</MenuItem>
            <MenuItem value="24">24 Hours</MenuItem>
            <MenuItem value="48">2 Days</MenuItem>
            <MenuItem value="96">4 Days</MenuItem>
            <MenuItem value="168">1 Week</MenuItem>
          </Select>
          {error.time && (
            <div className={styles.error}>
              <ErrorOutlineIcon className={styles.errIcon} />
              {error.time}
            </div>
          )}
        </div>
      </main>
      <footer className={styles.buttons}>
        <Button variant="contained" className={styles.backBtn} onClick={handleBack}>
          Back
        </Button>
        <Button variant="contained" className={styles.continueBtn} onClick={handleConfirm} disabled={singlePayLoading}>
          {singlePayLoading ? <CircularProgress size={24} style={{ color: "white" }} /> : "Confirm"}
        </Button>
      </footer>
    </div>
  );
}
